import React, { type ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { Resizable } from 'react-resizable';
import styled from 'types/theme.types';
import {
  NAVBAR_MAX_WIDTH,
  NAVBAR_MIN_WIDTH,
  useNavbarCollapse
} from 'components/layout/navbar/navbar-collapse-context';

const KEYBOARD_STEP = 16;

interface NavbarResizableProps {
  onResizingChange: (isResizing: boolean) => void;
  children: ReactElement;
}

const NavbarResizable = ({ onResizingChange, children }: NavbarResizableProps) => {
  const intl = useIntl();
  const { isCollapsed, isResizable, expandedWidth, setExpandedWidth } = useNavbarCollapse();

  const canResize = isResizable && !isCollapsed;

  const keyToWidth: Record<string, number> = {
    ArrowLeft: expandedWidth - KEYBOARD_STEP,
    ArrowRight: expandedWidth + KEYBOARD_STEP,
    Home: NAVBAR_MIN_WIDTH,
    End: NAVBAR_MAX_WIDTH
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!(event.key in keyToWidth)) return;
    event.preventDefault();
    setExpandedWidth(keyToWidth[event.key]);
  };

  return (
    <Resizable
      width={expandedWidth}
      height={0}
      axis="x"
      resizeHandles={canResize ? ['e'] : []}
      minConstraints={[NAVBAR_MIN_WIDTH, 0]}
      maxConstraints={[NAVBAR_MAX_WIDTH, 0]}
      onResizeStart={() => onResizingChange(true)}
      onResize={(_event, { size }) => setExpandedWidth(size.width, { persist: false })}
      onResizeStop={(_event, { size }) => {
        setExpandedWidth(size.width);
        onResizingChange(false);
      }}
      handle={
        <Handle
          data-e2e="navbar-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label={intl.formatMessage({ id: 'navigation.resize' })}
          aria-valuenow={expandedWidth}
          aria-valuemin={NAVBAR_MIN_WIDTH}
          aria-valuemax={NAVBAR_MAX_WIDTH}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onDoubleClick={() => setExpandedWidth(NAVBAR_MIN_WIDTH)}
        />
      }>
      {children}
    </Resizable>
  );
};

const Handle = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 6px;
  z-index: 1;
  cursor: col-resize;
  touch-action: none;

  &:hover,
  &:focus-visible {
    outline: none;
    background-color: var(--surface-navigation-navigation-item);
  }
`;

export default NavbarResizable;
